import { getDataAttributes } from "@utilities/selectUtils"
import React from "react"

import type {
  SelectItem,
  SelectOptionsGroup,
  SelectOptionWithNodeLabel,
  SelectOptionWithTextLabel,
} from "./Select"

interface OptionProps {
  option: SelectOptionWithNodeLabel | SelectOptionWithTextLabel
}

interface SelectOptionsProps {
  items: SelectItem[]
  supportsBaseSelect?: boolean
}

function isGroup(item: SelectItem): item is SelectOptionsGroup {
  return "options" in item
}

function getOptionLabel(option: {
  label: React.ReactNode
  textValue?: string
  value?: string
}): string {
  if (typeof option.label === "string") return option.label
  return option.textValue ?? option.value ?? ""
}

function Option({ option }: OptionProps) {
  return (
    <option
      {...getDataAttributes(option)}
      disabled={option.disabled}
      value={option.value}
    >
      {getOptionLabel(option)}
    </option>
  )
}

export function SelectOptions({
  items,
  supportsBaseSelect = false,
}: SelectOptionsProps): React.ReactNode {
  return items.map((item, index) => {
    if (isGroup(item)) {
      return (
        <optgroup
          key={`${item.label}-${index}`}
          disabled={item.disabled}
          label={item.label}
        >
          {supportsBaseSelect && (
            <legend>
              <span>{item.label}</span>
            </legend>
          )}
          {item.options.map((opt, optIndex) => {
            if (opt.divider) {
              return <hr key={`divider-${optIndex}`} />
            }
            return <Option key={opt.value ?? `opt-${optIndex}`} option={opt} />
          })}
        </optgroup>
      )
    }

    if (item.divider) {
      return <hr key={`divider-${index}`} />
    }

    return <Option key={item.value ?? `opt-${index}`} option={item} />
  })
}
